// src/pages/WebsiteSponsors.js
import React, { useState, useEffect } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  ListItemText,
  IconButton,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

// Firebase imports
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, auth, storage } from "../firebase";

const useStyles = makeStyles((theme) => ({
  form: {
    border: "1px solid #ccc",
    padding: 16,
    borderRadius: 4,
    marginBottom: 32,
  },
  input: {
    display: "block",
    marginBottom: 16,
  },
  thumbnail: {
    width: 100,
    height: "auto",
    objectFit: "contain",
    marginRight: 16,
  },
  orderButtons: {
    display: "flex",
    flexDirection: "column",
    marginRight: 16,
  },
  fileInput: {
    marginTop: 16,
  },
}));

function WebsiteSponsors() {
  const classes = useStyles();

  const [sponsors, setSponsors] = useState([]);
  // Add "url" to the form data
  const [formData, setFormData] = useState({
    name: "",
    width: "150px",
    url: "", // new field
  });
  const [imageFile, setImageFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");

  // Listen to Firestore
  useEffect(() => {
    const q = query(collection(db, "sponsors"), orderBy("order", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const sponsorsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSponsors(sponsorsData);
    });
    return () => unsubscribe();
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  // Upload image to Firebase Storage
  async function uploadImage(file) {
    if (!file) return "";
    const storageRef = ref(storage, `sponsors/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  }

  // Add/Update sponsor
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!auth.currentUser) {
      setMessage("You must be signed in to manage sponsors.");
      return;
    }

    try {
      let newImageUrl = "";
      if (imageFile) {
        newImageUrl = await uploadImage(imageFile);
      }

      if (editingId) {
        // Updating existing sponsor
        const sponsorRef = doc(db, "sponsors", editingId);
        const oldSponsor = sponsors.find((s) => s.id === editingId);
        const finalImageUrl = newImageUrl || (oldSponsor?.image ?? "");

        await updateDoc(sponsorRef, {
          name: formData.name,
          width: formData.width,
          url: formData.url, // store new/updated URL
          image: finalImageUrl,
        });
        setMessage("Sponsor updated successfully!");
      } else {
        // Creating new sponsor
        const newOrder = sponsors.length
          ? sponsors[sponsors.length - 1].order + 1
          : 0;
        await addDoc(collection(db, "sponsors"), {
          name: formData.name,
          width: formData.width,
          url: formData.url, // store sponsor website
          image: newImageUrl,
          order: newOrder,
        });
        setMessage("Sponsor added successfully!");
      }

      // Reset form
      setFormData({
        name: "",
        width: "150px",
        url: "",
      });
      setImageFile(null);
      setEditingId(null);
    } catch (error) {
      console.error("Error adding/updating sponsor:", error);
      setMessage("Error occurred while saving sponsor.");
    }
  };

  // Edit sponsor => populate form with existing data
  const handleEdit = (sponsor) => {
    setFormData({
      name: sponsor.name || "",
      width: sponsor.width || "150px",
      url: sponsor.url || "",
    });
    setImageFile(null);
    setEditingId(sponsor.id);
  };

  // Delete sponsor doc
  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "sponsors", id));
      setMessage("Sponsor deleted successfully!");
    } catch (error) {
      console.error("Error deleting sponsor:", error);
      setMessage("Error occurred while deleting sponsor.");
    }
  };

  // Reordering
  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const current = sponsors[index];
    const previous = sponsors[index - 1];
    try {
      await updateDoc(doc(db, "sponsors", current.id), {
        order: previous.order,
      });
      await updateDoc(doc(db, "sponsors", previous.id), {
        order: current.order,
      });
    } catch (err) {
      console.error("Error moving up:", err);
      setMessage("Error occurred while reordering.");
    }
  };

  const handleMoveDown = async (index) => {
    if (index === sponsors.length - 1) return;
    const current = sponsors[index];
    const next = sponsors[index + 1];
    try {
      await updateDoc(doc(db, "sponsors", current.id), {
        order: next.order,
      });
      await updateDoc(doc(db, "sponsors", next.id), {
        order: current.order,
      });
    } catch (err) {
      console.error("Error moving down:", err);
      setMessage("Error occurred while reordering.");
    }
  };

  return (
    <Container>
      <Box my={4}>
        <Typography variant="h4" gutterBottom>
          Manage Sponsors
        </Typography>

        <form onSubmit={handleSubmit} className={classes.form}>
          <TextField
            fullWidth
            label="Sponsor Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Website URL"
            name="url"
            value={formData.url}
            onChange={handleChange}
            margin="normal"
            placeholder="https://example.com"
          />

          <TextField
            fullWidth
            label="Image Width (e.g. 150px)"
            name="width"
            value={formData.width}
            onChange={handleChange}
            margin="normal"
          />

          <Typography variant="body2" gutterBottom>
            Upload a sponsor image logo, square/portrait logos work best:
          </Typography>
          <input
            className={classes.fileInput}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
          />

          <Button type="submit" variant="contained" color="primary" sx={{ mt: 2 }}>
            {editingId ? "Update Sponsor" : "Add Sponsor"}
          </Button>
        </form>

        {message && (
          <Typography variant="subtitle1" color="secondary">
            {message}
          </Typography>
        )}

        <Box my={4}>
          <Typography variant="h5" gutterBottom>
            Current Sponsors
          </Typography>
          {sponsors.map((sponsor, index) => (
            <Box key={sponsor.id} display="flex" alignItems="center" mb={2}>
              <Box className={classes.orderButtons}>
                <IconButton
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                >
                  <ArrowUpwardIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleMoveDown(index)}
                  disabled={index === sponsors.length - 1}
                >
                  <ArrowDownwardIcon />
                </IconButton>
              </Box>

              <img
                src={sponsor.image}
                alt={sponsor.name}
                className={classes.thumbnail}
              />
              <ListItemText
                primary={sponsor.name}
                secondary={`width: ${sponsor.width}\nURL: ${sponsor.url || "N/A"}`}
              />

              <IconButton onClick={() => handleEdit(sponsor)}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleDelete(sponsor.id)}>
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  );
}

export default WebsiteSponsors;
